import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  LinearProgress,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

class PromotionsOpenClaimsByPromotion extends React.Component {

  state = {
    order: 'asc',
    orderBy: null,
  };

  handleRequestSort = (property) => {
    const isAscending = this.state.orderBy === property && this.state.order === 'asc';
    this.setState({ order: isAscending ? 'desc' : 'asc', orderBy: property });
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const stringValue = String(value);
    const trimmedValue = stringValue.trimStart();
    const numericValue = /^-?\d+(?:\.\d+)?%?$/.test(trimmedValue.trim());
    const safeValue = !numericValue && /^[=+\-@\t\r]/.test(trimmedValue) ? `'${stringValue}` : stringValue;
    const cleanValue = safeValue.replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.props.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';
    const meta = this.props.meta || {};

    const totalOpenClaims = rows.reduce(
      (sum, row) => sum + Number(row.open_claims || 0),
      0
    );

    const totalConsumedClaims = rows.reduce(
      (sum, row) => sum + Number(row.consumed_claims || 0),
      0
    );

    const totalClaimsCreated = rows.reduce(
      (sum, row) => sum + Number(row.claims_created || 0),
      0
    );

    const weightedUnusedShare = totalClaimsCreated > 0
      ? (totalOpenClaims / totalClaimsCreated) * 100
      : 0;

    const highRiskPromotions = rows.filter(
      (row) => Number(row.unused_share || 0) > 50
    ).length;

    const headers = [
      { key: 'promotion_name', label: 'Promotion' },
      { key: 'open_claims', label: 'Open' },
      { key: 'consumed_claims', label: 'Used' },
      { key: 'claims_created', label: 'Eligible claims as of period end' },
      { key: 'unused_share', label: 'Unused %' },
    ];

    const csvRows = [
      ['Open claims by promotion'].map((value) => this.csvValue(value)).join(','),
      ['Period mode', periodMode].map((value) => this.csvValue(value)).join(','),
      ['As of date', meta.dateTo || ''].map((value) => this.csvValue(value)).join(','),
      ['Timezone', meta.timezone || ''].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total open claims', totalOpenClaims].map((value) => this.csvValue(value)).join(','),
      ['Total used claims', totalConsumedClaims].map((value) => this.csvValue(value)).join(','),
      ['Total eligible claims as of period end', totalClaimsCreated].map((value) => this.csvValue(value)).join(','),
      ['Weighted unused %', `${weightedUnusedShare.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Promotions with unused % over 50%', highRiskPromotions].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (header.key === 'unused_share') {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute(
      'download',
      `open_claims_by_promotion_${meta.dateTo || periodMode}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.props.data || [];
    const meta = this.props.meta || {};
    const alignedToPeriodEnd = this.props.alignedToPeriodEnd;
    const { order, orderBy } = this.state;
    const sortedRows = orderBy
      ? [...rows].sort((a, b) => {
          const aValue = orderBy === 'promotion_name'
            ? String(a.promotion_name || '').toLocaleLowerCase()
            : Number(a[orderBy] || 0);
          const bValue = orderBy === 'promotion_name'
            ? String(b.promotion_name || '').toLocaleLowerCase()
            : Number(b[orderBy] || 0);
          if (aValue < bValue) return order === 'asc' ? -1 : 1;
          if (aValue > bValue) return order === 'asc' ? 1 : -1;
          return 0;
        })
      : rows;

    const sortLabel = (property, label) => (
      <TableSortLabel
        active={orderBy === property}
        direction={orderBy === property ? order : 'asc'}
        onClick={() => this.handleRequestSort(property)}
      >
        {label}
      </TableSortLabel>
    );

    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.25, sm: 2 },
            '&:last-child': { pb: { xs: 1.25, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: '#111827',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {alignedToPeriodEnd ? 'Open claims by active promotion' : 'Open claims by promotion'}
                  </Typography>
                  <Tooltip
                    title={alignedToPeriodEnd
                      ? 'Eligible claims active at the selected period end. Unused share above 50% is highlighted red.'
                      : 'Claims issued in the selected period that remain unused. Unused share above 50% is highlighted red.'}
                    arrow
                    describeChild
                    enterTouchDelay={0}
                    leaveTouchDelay={5000}
                  >
                    <IconButton
                      size="small"
                      aria-label="About open claims by promotion"
                      sx={{ p: 0.25, color: '#64748b' }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>

              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon />}
                disabled={!rows.length}
                onClick={this.handleExportCsv}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  minWidth: 64,
                  px: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  color: '#111827',
                  borderColor: 'rgba(15, 23, 42, 0.14)',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: 'rgba(15, 23, 42, 0.24)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                    ml: 0,
                  },
                }}
              >
                CSV
              </Button>
            </Stack>

            <TableContainer
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: 'auto',
                borderRadius: '12px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: { xs: 620, lg: 560 },
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.7rem', sm: '0.78rem', lg: '0.72rem', xl: '0.76rem' },
                    py: { xs: 0.7, sm: 0.9 },
                    px: { xs: 0.75, sm: 1, lg: 0.65, xl: 0.8 },
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableCell-root:first-of-type': {
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: '#ffffff',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 800,
                    color: '#1a237e',
                    bgcolor: '#f8fafc',
                  },
                  '& .MuiTableCell-head:first-of-type': {
                    zIndex: 3,
                    bgcolor: '#f8fafc',
                  },
                  '& .MuiTableRow-hover:hover .MuiTableCell-body:first-of-type': {
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{sortLabel('promotion_name', 'Promotion')}</TableCell>
                    <TableCell align="right">{sortLabel('open_claims', 'Open')}</TableCell>
                    <TableCell align="right">{sortLabel('consumed_claims', 'Used')}</TableCell>
                    <TableCell align="right">{sortLabel('claims_created', 'Eligible')}</TableCell>
                    <Tooltip title="Red indicates that more than 50% of eligible claims remained unused at period end." arrow>
                      <TableCell align="right">{sortLabel('unused_share', 'Unused')}</TableCell>
                    </Tooltip>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedRows.map((row, index) => {
                    const unusedShare = Number(row.unused_share || 0);
                    const promotionName = row.promotion_name || 'Unknown';

                    return (
                      <TableRow key={`${row.promotion_iref || 'promo'}-${index}`} hover>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: '#111827',
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <Tooltip title={promotionName} arrow>
                            <Box component="span">{promotionName}</Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            color: unusedShare > 50 ? '#b91c1c' : '#111827',
                          }}
                        >
                          {thousandsSeparator(Number(row.open_claims || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.consumed_claims || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.claims_created || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right" sx={{ minWidth: { xs: 120, lg: 100 } }}>
                          <Stack direction="row" spacing={{ xs: 1, lg: 0.65 }} alignItems="center" justifyContent="flex-end">
                            <Box sx={{ width: { xs: 54, lg: 38 } }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(unusedShare, 100)}
                                sx={{
                                  height: 6,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(96, 125, 139, 0.10)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    bgcolor: unusedShare > 50 ? '#b91c1c' : '#607d8b',
                                  },
                                }}
                              />
                            </Box>

                            <Box
                              sx={{
                                minWidth: 42,
                                fontWeight: 800,
                                color: unusedShare > 50 ? '#b91c1c' : '#111827',
                              }}
                            >
                              {formatPercent(unusedShare)}
                            </Box>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {alignedToPeriodEnd
                          ? 'No eligible open claims were found as of the selected period end.'
                          : 'No open claims data was found for the selected period.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default PromotionsOpenClaimsByPromotion;
